/**
 * ModulePrivilegesPage — dedicated sidebar page for Tier × Module privilege management.
 *
 * Sub-tabs:
 *   Matrix      — TierMatrixEditor (the main feature)
 *   UAMS Master — manage modules / submodules / screens tree
 *   Tier Integrity — consistency checker
 */
import React, { useState, useCallback } from 'react';
import { Grid3X3, Layers, ShieldCheck, RefreshCw, CheckCircle, AlertTriangle } from 'lucide-react';
import TierMatrixEditor   from '../components/TierMatrixEditor';
import UAMSMasterManager  from '../components/UAMSMasterManager';
import TierIntegrityPanel from '../components/TierIntegrityPanel';
import '../styles/erp-theme.css';
import { API } from '../lib/api';

const getToken = () => sessionStorage.getItem('erp_security_token') || localStorage.getItem('erp_security_token');

const SUB_TABS = [
  { id: 'matrix',    label: 'Privilege Matrix',  icon: Grid3X3  },
  { id: 'uams',      label: 'UAMS Master',        icon: Layers   },
  { id: 'integrity', label: 'Tier Integrity',     icon: ShieldCheck },
];

export default function ModulePrivilegesPage() {
  const [sub,        setSub]        = useState('matrix');
  const [syncStatus, setSyncStatus] = useState(null); // null | 'running' | { ok, err, at }

  const syncAllDBs = useCallback(async () => {
    setSyncStatus('running');
    try {
      const res = await fetch(`${API}/uams/propagate-to-all`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
      });
      const d = await res.json();
      setSyncStatus({ running: !!d.accepted, ok: 0, err: 0, at: new Date().toLocaleTimeString() });
    } catch (e) {
      setSyncStatus({ running: false, ok: 0, err: 1, at: new Date().toLocaleTimeString() });
    }
  }, []);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 0 }}>

      {/* ── Propagate All DBs bar ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        padding: '6px 12px',
        background: 'var(--surface-2, #f8fafc)',
        borderBottom: '1px solid var(--border)',
        fontSize: '0.78rem', flexShrink: 0,
      }}>
        <RefreshCw size={11} color="var(--text-muted)" />
        <span style={{ color: 'var(--text-secondary)', flex: 1 }}>
          {syncStatus === null && 'Push tier privileges + module entitlements to all Template DBs and Client DBs.'}
          {syncStatus === 'running' && <span style={{ color: '#f59e0b' }}>Submitting request…</span>}
          {syncStatus && syncStatus !== 'running' && syncStatus.running && (
            <span style={{ color: '#f59e0b', display:'flex', alignItems:'center', gap:4 }}>
              <RefreshCw size={10} className="spin" /> Propagating to all databases in background…
            </span>
          )}
          {syncStatus && syncStatus !== 'running' && !syncStatus.running && syncStatus.err === 0 && (
            <span style={{ color: '#22c55e', display:'flex', alignItems:'center', gap:4 }}>
              <CheckCircle size={10} /> Sync accepted at {syncStatus.at} — running in background
            </span>
          )}
          {syncStatus && syncStatus !== 'running' && syncStatus.err > 0 && (
            <span style={{ color: '#ef4444', display:'flex', alignItems:'center', gap:4 }}>
              <AlertTriangle size={10} /> Sync failed — check server logs
            </span>
          )}
        </span>
        <button
          className="erp-btn erp-btn-primary"
          onClick={syncAllDBs}
          disabled={syncStatus === 'running' || (syncStatus && syncStatus !== 'running' && syncStatus.running)}
          data-testid="modpriv-sync-all-dbs"
          title="Push all tier privileges + module matrix to every Template DB and Client DB"
          style={{ whiteSpace: 'nowrap', fontSize: '0.78rem', padding: '4px 10px', gap: 5 }}
        >
          <RefreshCw size={11} className={syncStatus === 'running' ? 'spin' : ''} />
          {(syncStatus === 'running' || (syncStatus && syncStatus !== 'running' && syncStatus.running))
            ? 'Syncing…' : 'Sync All DBs'}
        </button>
      </div>

      {/* sub-tab strip */}
      <div style={{
        display: 'flex', gap: 0,
        borderBottom: '2px solid var(--border, #e2e8f0)',
        background: 'var(--surface-2, #f8fafc)',
        padding: '0 4px', flexShrink: 0, marginTop: 0,
      }}>
        {SUB_TABS.map(t => {
          const Icon   = t.icon;
          const active = sub === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setSub(t.id)}
              data-testid={`modpriv-subtab-${t.id}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 16px', border: 'none', cursor: 'pointer',
                background: 'none',
                borderBottom: `3px solid ${active ? '#3b82f6' : 'transparent'}`,
                color: active ? '#3b82f6' : '#64748b',
                fontWeight: active ? 700 : 500,
                fontSize: '0.86rem', transition: 'all 0.15s',
                marginBottom: -2,
              }}
            >
              <Icon size={13} />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* content */}
      <div style={{ flex: 1, overflow: 'auto', padding: sub === 'matrix' ? 0 : '12px 16px' }}>
        {sub === 'matrix'    && <TierMatrixEditor />}
        {sub === 'uams'      && <UAMSMasterManager />}
        {sub === 'integrity' && <TierIntegrityPanel />}
      </div>
    </div>
  );
}

